import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaLinkedinIn,
  FaFacebookF,
  FaTwitter,
  FaEnvelope,
} from "react-icons/fa";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField, Button } from "@mui/material";

const Footer = () => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    phone: Yup.string()
      .matches(/^[0-9]+$/, "Must be only digits")
      .required("Phone is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required")
      .test(
        "no-yopmail",
        "Emails from yopmail.com are not allowed.",
        (value) => {
          if (!value) {
            return true;
          }
          const domain = value.substring(value.lastIndexOf("@") + 1);
          return domain.toLowerCase() !== "yopmail.com";
        }
      ),
    message: Yup.string().required("Message is required"),
  });

  const initialValues = {
    name: "",
    phone: "",
    email: "",
    message: "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await fetch(
        "https://formsubmit.co/ajax/deepti@doctususa.com",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...values,
            _subject: `New Contact Request from ${values.name}`,
            _template: "table",
            _autoresponse: `Thank you ${values.name}, your message has been received. We will get back to you shortly.`,
          }),
        }
      );

      const data = await response.json();

      if (data.success === "true") {
        setShowSuccess(true);
        setShowError(false);
        resetForm();
        // Hide success message after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        setErrorMessage("Failed to send message. Please try again.");
        setShowError(true);
        setShowSuccess(false);
        resetForm(); // Reset form on error too
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage("An error occurred. Please try again later.");
      setShowError(true);
      setShowSuccess(false);
      resetForm(); // Reset form on error too
    } finally {
      setSubmitting(false);
    }
  };

  const SuccessMessage = () => (
    <div className="fixed top-24 right-4 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded shadow-lg max-w-sm animate-fade-in">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium">Message sent successfully!</p>
          <p className="text-xs text-green-600 mt-1">We'll get back to you soon.</p>
        </div>
        <div className="ml-auto pl-3">
          <button
            onClick={() => setShowSuccess(false)}
            className="text-green-400 hover:text-green-600"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const ErrorMessage = () => (
    <div className="fixed top-24 right-4 z-50 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg max-w-sm animate-fade-in">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium">Error</p>
          <p className="text-xs text-red-600 mt-1">{errorMessage}</p>
        </div>
        <div className="ml-auto pl-3">
          <button
            onClick={() => setShowError(false)}
            className="text-red-400 hover:text-red-600"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Careers", href: "/working" },
    { name: "Contact Us", href: "/contact" },
  ];

  const services = [
    { name: "Revenue Cycle Services", href: "/revenue-cycle" },
    { name: "Medical Coding Services", href: "/medical-coding" },
    // { name: "Payer Services", href: "/payer-services" },
    // { name: "HEDIS Measures Reporting", href: "/hedis" },
    { name: "Document Management Services", href: "/document-management" },
  ];

  const revenueCycleServices = [
    { name: "Overview", href: "/revenue-cycle" },
    // {
    //   name: "Eligibility and Benefits Verification, and Prior Authorization",
    //   href: "/eligibility",
    // },
    // { name: "Patient Scheduling", href: "/patient-scheduling" },
    // { name: "Patient Registration", href: "/patient-registration" },
    // { name: "Medical Coding", href: "/medical-coding" },
    // {
    //   name: "Claim Submission & Rejection Management",
    //   href: "/claim-submission",
    // },
    // { name: "Payment Posting", href: "/payment-posting" },
    // { name: "Accounts Receivables Management", href: "/accounts-receivables" },
    // { name: "Denial Management", href: "/denial-management" },
    // { name: "Credit Balance and Refunds Processing", href: "/credit-balance" },
    // { name: "Revenue Cycle Analytics", href: "/revenue-cycle-analytics" },
    // {
    //   name: "Provider Credentialing and Enrollment",
    //   href: "/provider-credentialing",
    // },
  ];

  const socialIcons = [
    { icon: <FaLinkedinIn />, href: "https://linkedin.com" },
    { icon: <FaFacebookF />, href: "https://facebook.com" },
    { icon: <FaTwitter />, href: "https://twitter.com" },
    { icon: <FaEnvelope />, href: "mailto:info@doctusrcm.com" },
  ];

  const addresses = [
    {
      location: "US ADDRESS",
      lines: [
       "Kennett Square, PA, 19348"
        // "PO Box 58887, Salt Lake City",
        // "Draper, UT 84158",
        // "1 (888) 736-3611",
        // "info@doctusrcm.com",
      ],
    },
    // {
    //   location: "INDIA - CHENNAI",
    //   lines: [
    //     "Workafella Business Center,",
    //     "Suite #312, #10,Uthamar Gandhi Salai,",
    //     "Nungambakkam, Chennai - 600034.",
    //     "+91 44 4502 4444",
    //   ],
    // },
    {
      location: "INDIA - HYDERABAD",
      lines: [
        "6th Floor, Solitaire Plaza, Image Hospitals Ln,", 
        "Pratap Nagar, Nagarjuna Nagar colony, Yella Reddy Guda,",
         "Hyderabad, Telangana 500073."
        // "Mahaveer Techno Park, SEEC Tower, 2nd Floor,",
        // "Plot No.6, APIIC Layout, Madhapur, Hyderabad,",
        // "Telangana 500081 &nbsp;&nbsp;&nbsp; PH: 040-6939 3335",
      ],
    },
    // {
    //   location: "INDIA - COIMBATORE",
    //   lines: [
    //     "Block A2-Ground Floor, Span Ventures,",
    //     "Rathinam Technopark- SEZ",
    //     "Pollachi Road, Eachanari, Coimbatore - 641021",
    //   ],
    // },
  ];

  return (
    <footer className="bg-[#f2f2f2] text-gray-800 pt-16 pb-12 relative">
      {showSuccess && <SuccessMessage />}
      {showError && <ErrorMessage />}
      <div className="container mx-auto px-4">
        <div className="flex justify-center mb-8">
          {socialIcons.map((social, index) => (
            <a
              key={index}
              href={social.href}
              className="text-gray-600 hover:text-[#4b675c] mx-3 text-xl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {social.icon}
            </a>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Get in Touch */}
          <div>
            <h3 className="text-lg font-bold text-[#4b675c] mb-4">
              GET IN TOUCH
            </h3>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, touched, errors }) => (
                <Form>
                  <Field
                    name="name"
                    as={TextField}
                    label={
                      <>
                        Name <span style={{ color: "red" }}>*</span>
                      </>
                    }
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                  <Field
                    name="phone"
                    as={TextField}
                    label={
                      <>
                        Phone <span style={{ color: "red" }}>*</span>
                      </>
                    }
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                  <Field
                    name="email"
                    as={TextField}
                    label={
                      <>
                        Email <span style={{ color: "red" }}>*</span>
                      </>
                    }
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                  <Field
                    name="message"
                    as={TextField}
                    label={
                      <>
                        Message <span style={{ color: "red" }}>*</span>
                      </>
                    }
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    multiline
                    rows={4}
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      backgroundColor: "#4b675c",
                      "&:hover": { backgroundColor: "#4c5463" },
                    }}
                  >
                    SUBMIT
                  </Button>
                </Form>
              )}
            </Formik>
          </div>

          {/* Quick Links & Services */}
          <div>
            <h3 className="text-lg font-bold text-[#4b675c] mb-4">
              QUICK LINKS
            </h3>
            <ul>
              {quickLinks.map((link) => (
                <li key={link.name} className="mb-2">
                  <Link to={link.href} className="hover:text-[#4b675c]">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="text-lg font-bold text-[#4b675c] mt-8 mb-4">
              SERVICES
            </h3>
            <ul>
              {services.map((service) => (
                <li key={service.name} className="mb-2">
                  <Link to={service.href} className="hover:text-[#4b675c]">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Revenue Cycle Services */}
          <div>
            <h3 className="text-lg font-bold text-[#4b675c] mb-4">
              REVENUE CYCLE SERVICES
            </h3>
            <ul>
              {revenueCycleServices.map((service) => (
                <li key={service.name} className="mb-2">
                  <Link
                    to={service.href}
                    className="hover:text-[#4b675c] text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* US Address & India Locations */}
          <div>
            {addresses.map((address, index) => (
              <div key={address.location} className={index !== 0 ? "mt-8" : ""}>
                <h3 className="text-lg font-bold text-[#4b675c] mb-4">
                  {address.location}
                </h3>
                {address.lines.map((line, lineIndex) => (
                  <p
                    key={lineIndex}
                    className="text-sm"
                    dangerouslySetInnerHTML={{ __html: line }}
                  ></p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
